import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { api } from '../api';
import { Card, CardHeader, Badge, Skeleton, Table, PageWrap } from '../components/UI';
import AgentSyncBar from '../components/AgentSyncBar';
import { useOnSyncComplete } from '../hooks/useSyncStatus';
import { AUDIT_STATUS_CONFIG, TERMINAL_STATUSES } from '../auditStatus';
import '../styles/EngagementDetail.css';

function EngagementStatusBadge({ status }) {
  const c = AUDIT_STATUS_CONFIG[status] || AUDIT_STATUS_CONFIG.synced;
  return (
    <span
      className="ed-eng-status-badge"
      style={{ background: c.bg, color: c.color, border: `1px solid ${c.color}33` }}
    >
      {c.label}
    </span>
  );
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
    timeZone: 'Asia/Kolkata',
  }).replace(',', '') + ' IST';
}


// ── Engagement comment thread ──────────────────────────────────────────────────
const ROLE_PILL = {
  ca:       { label: 'CA',       color: 'var(--accent)', bg: 'var(--blue-bg)'   },
  admin:    { label: 'Admin',    color: 'var(--purple)', bg: 'var(--purple-bg)' },
  customer: { label: 'Customer', color: 'var(--green)',  bg: 'var(--green-bg)'  },
};

const EVENT_ICON = {
  engagement_created: '✦',
  ca_assigned:        '→',
  status_change:      '◈',
};

function fmtCommentTime(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Kolkata',
  });
}

function SystemEvent({ c }) {
  const icon = EVENT_ICON[c.event_type] || '·';
  // Split body at the first double-newline to separate the system headline from any user note
  const parts = c.body.split('\n\n');
  const headline = parts[0];
  const note = parts.slice(1).join('\n\n').trim();

  return (
    <div className="ed-system-event">
      <span className="ed-system-icon">{icon}</span>
      <span className="ed-system-headline">{headline}</span>
      {note && <span className="ed-system-note">{note}</span>}
      <span className="ed-system-time">{fmtCommentTime(c.created_at)}</span>
    </div>
  );
}

function EngagementComments({ engagementId }) {
  const [comments,  setComments]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [fetchErr,  setFetchErr]  = useState('');
  const [text,      setText]      = useState('');
  const [posting,   setPosting]   = useState(false);
  const [postError, setPostError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    setLoading(true); setFetchErr('');
    api.listEngagementComments(engagementId)
      .then(data => { setComments(Array.isArray(data) ? data : []); })
      .catch(e => { console.error('[EngagementComments] fetch failed:', e.message); setFetchErr(e.message); })
      .finally(() => setLoading(false));
  }, [engagementId]);

  useEffect(() => {
    if (!loading) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [comments, loading]);

  const handlePost = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setPosting(true); setPostError('');
    try {
      const comment = await api.postEngagementComment(engagementId, trimmed);
      setComments(c => [...c, comment]);
      setText('');
    } catch (e) {
      setPostError(e.message);
    } finally {
      setPosting(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handlePost();
  };

  const userComments = comments.filter(c => !c.is_system);

  return (
    <div className="ed-comments">
      <div className="ed-comments-title">
        Discussion &amp; Activity
        {userComments.length > 0 && (
          <span className="ed-comments-count">{userComments.length}</span>
        )}
      </div>

      <div className="ed-comments-list">
        {loading ? (
          <div className="ed-comments-empty">Loading…</div>
        ) : fetchErr ? (
          <div className="ed-comments-empty" style={{ color: '#dc2626' }}>
            Could not load activity: {fetchErr}
          </div>
        ) : comments.length === 0 ? (
          <div className="ed-comments-empty">No activity yet.</div>
        ) : (
          comments.map(c => {
            if (c.is_system) return <SystemEvent key={c.id} c={c} />;

            const pill = ROLE_PILL[c.author_role] || ROLE_PILL.customer;
            return (
              <div key={c.id} className="ed-comment">
                <div className="ed-comment-meta">
                  <span className="ed-comment-author">{c.author_name || c.author_email}</span>
                  <span
                    className="ed-comment-role"
                    style={{ background: pill.bg, color: pill.color }}
                  >
                    {pill.label}
                  </span>
                  <span className="ed-comment-time">{fmtCommentTime(c.created_at)}</span>
                </div>
                <div className="ed-comment-body">{c.body}</div>
              </div>
            );
          })
        )}
        <div ref={bottomRef} />
      </div>

      <div className="ed-comment-input-wrap">
        <textarea
          className="ed-comment-textarea"
          rows={3}
          placeholder="Write a comment… (Ctrl+Enter to send)"
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={handleKey}
          disabled={posting}
        />
        {postError && <div className="ed-comment-error">{postError}</div>}
        <div className="ed-comment-actions">
          <span className="ed-comment-hint">{text.length}/2000</span>
          <button
            className="ed-comment-send"
            onClick={handlePost}
            disabled={posting || !text.trim()}
          >
            {posting ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function EngagementDetail({ engagementId, userRole, useCustomerApi, onBack, onOpenSync }) {
  const isCA = (userRole?.isCA || userRole?.isAdmin || false) && !useCustomerApi;

  const { data, loading, error, refetch } = useApi(
    () => isCA ? api.getEngagement(engagementId) : api.getMyEngagement(engagementId),
    [engagementId, isCA],
  );

  useOnSyncComplete(() => refetch());

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting,      setDeleting]      = useState(false);
  const [deleteErr,     setDeleteErr]     = useState('');

  const eng   = data?.engagement;
  const syncs = data?.syncs || [];

  const handleSyncClick = useCallback((sync) => {
    onOpenSync({
      source_id:    eng?.source_id,
      sync_id:      sync.sync_id,
      company_name: eng?.company_name,
    });
  }, [eng, onOpenSync]);

  const handleDelete = useCallback(async () => {
    setDeleting(true);
    setDeleteErr('');
    try {
      await api.deleteEngagement(engagementId);
      onBack();
    } catch (e) {
      setDeleteErr(e.message);
      setDeleting(false);
    }
  }, [engagementId, onBack]);

  return (
    <PageWrap>
      <button onClick={onBack} className="ed-back-btn">← Back to Engagements</button>

      {loading ? (
        <Skeleton rows={4} />
      ) : error ? (
        <div className="ed-error">Failed to load engagement: {error}</div>
      ) : eng ? (
        <>
          <div className="ed-header">
            <div className="ed-header-left">
              <div className="ed-eng-id">{eng.engagement_id || engagementId}</div>
              <div className="ed-company">{eng.company_name || '—'}</div>
              {eng.customer_name && (
                <div className="ed-customer">
                  {eng.customer_name}
                  {eng.customer_email && <span className="ed-customer-email"> · {eng.customer_email}</span>}
                </div>
              )}
              {eng.customer_pan && <div className="ed-pan">PAN: {eng.customer_pan}</div>}
              {(eng.creator_name || eng.ca_name) && (
                <div className="ed-creator">
                  Created by{' '}
                  <span className="ed-creator-name">
                    {eng.creator_name || eng.ca_name}
                  </span>
                  {(eng.creator_email || eng.ca_email) && (
                    <span className="ed-creator-email">
                      {' '}· {eng.creator_email || eng.ca_email}
                    </span>
                  )}
                </div>
              )}
              <div className="ed-created">
                Created {eng.created_at
                  ? new Date(eng.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })
                  : '—'}
              </div>
            </div>
            <div className="ed-header-right">
              <EngagementStatusBadge status={eng.status} />
              {isCA && !TERMINAL_STATUSES.includes(eng.status) && (
                <div className="ed-delete-wrap">
                  {confirmDelete ? (
                    <div className="ed-delete-confirm">
                      <span className="ed-delete-confirm-text">Delete this engagement?</span>
                      {deleteErr && <div className="ed-delete-err">{deleteErr}</div>}
                      <div className="ed-delete-confirm-btns">
                        <button
                          className="ed-delete-cancel-btn"
                          onClick={() => { setConfirmDelete(false); setDeleteErr(''); }}
                        >
                          Cancel
                        </button>
                        <button
                          className="ed-delete-confirm-btn"
                          onClick={handleDelete}
                          disabled={deleting}
                        >
                          {deleting ? 'Deleting…' : 'Yes, Delete'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      className="ed-delete-btn"
                      onClick={() => setConfirmDelete(true)}
                    >
                      Delete
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {!['audit_completed', 'audit_abandoned'].includes(eng.status) &&
           (isCA ? eng.sync_responsibility === 'ca' : (eng.sync_responsibility || 'customer') === 'customer') && (
            <div style={{ marginBottom: 16 }}>
              <AgentSyncBar />
            </div>
          )}

          <Card>
            <CardHeader title={`${syncs.length} Sync Run${syncs.length !== 1 ? 's' : ''}`} />
            {syncs.length === 0 ? (
              <div className="ed-empty">
                {isCA
                  ? eng.sync_responsibility === 'ca'
                    ? 'No syncs yet. Use the sync bar above to push Tally data for this engagement.'
                    : 'No syncs yet for this engagement. Ask the customer to sync from Tally.'
                  : 'No Tally data synced yet for this engagement. Sync from your Tally Connector to get started.'}
              </div>
            ) : (
              <Table
                columns={[
                  {
                    label: 'Sync ID', key: 'sync_id',
                    render: r => (
                      <span className="ed-sync-id">
                        S-{String(r.sync_id || '').slice(-8).toUpperCase()}
                      </span>
                    ),
                  },
                  {
                    label: 'Period', key: 'period_from', noWrap: true,
                    render: r => (
                      <span className="ed-period">
                        {r.period_from && r.period_to ? `${r.period_from} – ${r.period_to}` : '—'}
                      </span>
                    ),
                  },
                  {
                    label: 'Synced At', key: 'synced_at', noWrap: true,
                    render: r => <span className="ed-date">{fmtDateTime(r.synced_at)}</span>,
                  },
                  {
                    label: 'Vouchers', key: 'voucher_count', right: true, mono: true,
                    render: r => (r.voucher_count ?? 0).toLocaleString('en-IN'),
                  },
                  {
                    label: 'Issues', key: 'issue_count', right: true, mono: true,
                    render: r => (r.issue_count ?? 0).toLocaleString('en-IN'),
                  },
                  {
                    label: 'Status', key: 'status',
                    render: r => (
                      <Badge
                        text={r.status || 'complete'}
                        variant={r.status === 'complete' ? 'green' : r.status === 'failed' ? 'red' : 'amber'}
                      />
                    ),
                  },
                  {
                    label: '', key: '_open',
                    render: r => (
                      <button
                        className="ed-open-btn"
                        onClick={e => { e.stopPropagation(); handleSyncClick(r); }}
                      >
                        Open Audit →
                      </button>
                    ),
                  },
                ]}
                rows={syncs}
                emptyMsg="No syncs found"
              />
            )}
          </Card>

          <EngagementComments engagementId={engagementId} />
        </>
      ) : (
        <div className="ed-error">Engagement not found.</div>
      )}
    </PageWrap>
  );
}
